import { moment } from '@lib/moment';
import noop from 'lodash/noop';
import { BehaviorSubject } from 'rxjs';
import { Bell } from 'phx-core';

import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { filters, mockNotifications, customLocaleKey } from './notification-utils';
import { mapNotification, NotificationsCenterService } from './notifications.service';
import { FILTER_TYPE, IFilterType, INotification } from './notification.interface';

import { ISidebarState, SidebarService } from '@app/services/sidebar.service';

@Component({
  selector: 'notification-center',
  templateUrl: 'notification-center.component.html',
  styleUrls: ['notification-center.component.scss'],
})
export class NotificationCenterComponent implements OnInit {
  @HostBinding('class.light-theme') @Input() public isLightTheme: boolean;
  @HostBinding('class.collapse-sidebar') public isSideBarCollapsed: boolean;
  @ViewChild('notificationList', { static: false }) public notificationList: ElementRef<HTMLDivElement>;
  @Input() public disableClick: boolean;
  public showError = false;
  public isDropdownOpen = false;
  public mockNotifications: INotification[] = mockNotifications.sort(this.sortNotifications);
  public notificationsSubject: BehaviorSubject<INotification[]> = new BehaviorSubject(this.notifications);
  public filterValue: IFilterType = filters.find(filter => filter.id === FILTER_TYPE.ALL);
  public filters = filters;
  public FILTER_TYPE = FILTER_TYPE;
  public isLoading = false;
  public readonly bellIcon: string = Bell;
  public currentFilterSelection: IFilterType;

  public get notifications(): INotification[] {
    return this.notificationsInternal;
  }

  private notificationsMap: Map<number, INotification> = new Map();
  private notificationsInternal: INotification[] = [];

  constructor(
    public sidebarService: SidebarService,
    private hostElement: ElementRef,
    private notificationsService: NotificationsCenterService,
  ) {}

  public ngOnInit(): void {
    this.sidebarService.stateListener.subscribe((state: ISidebarState) => {
      this.isSideBarCollapsed = state === 'collapsed';
    });
    this.currentFilterSelection = this.filters[0];
    this.registerCustomLocale();
    this.getNotifications();
    this.subscribeToPushNotifications();
  }

  public toggle(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    this.determineTopBannerHeight();
    if (this.isDropdownOpen) {
      this.markNotificationsSeen();
    }
  }

  @HostListener('window:click', ['$event'])
  public onLoseFocus(event: MouseEvent): void {
    if (!this.hostElement.nativeElement.contains(event.target)) this.closeIfOpen();
  }

  public closeIfOpen(): void {
    if (this.isDropdownOpen) this.toggle();
  }

  public markAllAsRead(): void {
    let hasUnread = false;
    this.notificationsMap.forEach((notification: INotification) => {
      if (!notification.read) hasUnread = true;
      notification.read = true;
    });
    if (hasUnread) {
      this.notificationsService
        .markAllNotificationsRead()
        .subscribe(noop);
    }
  }

  public onScrollToBottom(): void {
    if (this.currentFilterSelection.isFullyLoaded) return;
    this.currentFilterSelection.params.page_no++;
    this.getNotifications();
  }

  public onErrorClick(): void {
    this.showError = false;
    this.getNotifications();
  }

  public filterChange(selection: IFilterType): void {
    if (this.notificationList) {
      this.notificationList.nativeElement.scrollTop = 0;
    }
    this.currentFilterSelection = selection;
    if (!this.currentFilterSelection.hasReceivedInitialData) this.getNotifications();
    else this.setNotifications(selection);
  }

  private sortNotifications(a: INotification, b: INotification): number {
    return a.createdAt < b.createdAt ? 1 : -1;
  }

  private determineTopBannerHeight(): void {
    const bannerBar = document.querySelector('.banner-bar');
    const children = Array.from(bannerBar.children);
    const totalHeight = children.reduce((previous, current) => previous + current.getBoundingClientRect().height, 0);
    this.hostElement.nativeElement.style.setProperty('--banner-height', `${totalHeight}px`);
  }

  private registerCustomLocale(): void {
    const previousLocale = moment.locale();
    moment.locale(customLocaleKey, {
      relativeTime : {
        future: 'in %s',
        past: '%s',
        s: '%ds',
        m: '%dm',
        mm: '%dm',
        h: '%dh',
        hh: '%dh',
        d: '%dd',
        dd: '%dd',
        M: '%dmo',
        MM: '%dmo',
        y: '%dy',
        yy: '%dy',
      },
    });
    moment.locale(previousLocale);
  }

  private getNotifications(): void {
    if (!this.currentFilterSelection.hasReceivedInitialData) this.toggleLoadingStateAndEmitMockData();
    else this.toggleLoadingState();
    this.notificationsService.get(this.currentFilterSelection.params)
      .subscribe({
        next: this.handleSuccessfulResponse.bind(this),
        error: this.handleErrorResponse.bind(this),
      });
  }

  private handleSuccessfulResponse(newNotifications: INotification[]): void {
    this.showError = false;
    this.updateNotificationsMap(newNotifications);
    this.updateFilterNotifications(newNotifications, this.currentFilterSelection.id);
    this.currentFilterSelection.hasReceivedInitialData = true;
    this.setNotifications(this.currentFilterSelection);
    if (newNotifications.length < this.currentFilterSelection.params.per_page) this.currentFilterSelection.isFullyLoaded = true;
    this.toggleLoadingState();
    if (this.isDropdownOpen) this.markNotificationsSeen();
  }

  private handleErrorResponse(err: HttpErrorResponse): void {
    this.showError = true;
  }

  private toggleLoadingState(): void {
    this.isLoading = !this.isLoading;
  }

  private toggleLoadingStateAndEmitMockData(): void {
    this.toggleLoadingState();
    this.notificationsSubject.next(this.mockNotifications);
  }

  private markNotificationsSeen(): void {
    const unseenNotificationIds = this.notifications
      .filter((notification: INotification) => !notification.seen)
      .map((notification: INotification) => notification.id);

    if (unseenNotificationIds.length) {
      unseenNotificationIds.forEach((id: number) => {
        if (this.notificationsMap.has(id)) {
          this.notificationsMap.get(id).seen = true;
        }
      });
      this.notificationsSubject.next(this.notifications);
      this.notificationsService.markNotificationsSeen(unseenNotificationIds).subscribe(noop);
    }
  }

  private subscribeToPushNotifications(): void {
    this.notificationsService.getPushNotifications()
    .subscribe(data => {
      const newNotification = mapNotification({
        notification: JSON.parse(data.notification_center).notification_center_notification,
      });

      if (newNotification?.priority) {
        this.updateFilterNotifications([newNotification], FILTER_TYPE.IMPORTANT);
      }
      this.updateFilterNotifications([newNotification], FILTER_TYPE.ALL);
      this.updateNotificationsMap([newNotification]);
      this.setNotifications(this.currentFilterSelection);
      if (this.isDropdownOpen) this.markNotificationsSeen();
    });
  }

  private updateNotificationsMap(notifications: INotification[]): void {
    if (!notifications.length) return;
    notifications.forEach((notification: INotification) =>
      this.notificationsMap.set(notification.id, notification),
    );
  }

  private updateFilterNotifications(notifications: INotification[], filterType: FILTER_TYPE): void {
    if (!notifications.length) return;

    const filter: IFilterType = this.filters.find((f: IFilterType) => f.id === filterType);
    notifications.forEach((notification: INotification) =>
      filter?.notificationIds.add(notification.id),
    );
  }

  private setNotifications(filter: IFilterType): void {
    this.notificationsInternal = Array.from(filter.notificationIds)
      .map((id: number) => this.notificationsMap.get(id))
      .sort(this.sortNotifications);
    this.notificationsSubject.next([...this.notifications]);
  }
}
